import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import HighlighOffIcon from "@mui/icons-material/HighlightOff"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import CheckBox from "@mui/material/Checkbox"
import Collapse from "@mui/material/Collapse"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Divider from "@mui/material/Divider"
import Drawer from "@mui/material/Drawer"
import Fade from "@mui/material/Fade"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormLabel from "@mui/material/FormLabel"
import InputAdornment from "@mui/material/InputAdornment"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { styled } from "@mui/material/styles"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import Column from "../../../../common/components/Column"
import LengthLimitedTextField from "../../../../common/components/LengthLimitedTextField"
import Row from "../../../../common/components/Row"
import TitledSection from "../../../../common/components/TitledSection"
import SegmentTracker from "../../../../common/tracking"
import {
  selectCommissionTiers,
  selectEmailProviderAuthorized,
} from "../../reducer"
import { OFFER_ORDERED_STATUSES } from "../../utils"
import DialogHeader from "../DialogHeader"
import OfferStatusTextField, {
  useOfferStatusTextFieldState,
} from "../OfferStatusTextField"
import {
  TrialFinishedRoadblockDialogData,
  useTrialFinishedDialogUtils,
} from "../TrialFinishedRoadblockDialog"
import PerksEditor, { useSelectedPerks } from "./PerksEditor"

export default function CreateOfferDialog({ open, onClose, candidate }) {
  const [name, setName] = useState("")
  const [nameError, setNameError] = useState(null)
  const [introduction, setIntroduction] = useState("")
  const [introductionError, setIntroductionError] = useState(null)
  const { status, setStatus } = useOfferStatusTextFieldState()
  const [signingBonus, setSigningBonus] = useState(0)
  const [signingBonusError, setSigningBonusError] = useState(null)
  const commissionSplitTiersSetting = useSelector(selectCommissionTiers)

  const DEFAULT_COMMISSION_TIER = new CommssionTier()
  const [tierSelectionModel, setTierSelectionModel] = useState(
    PERFORMANCE_TIER_SELECTION_MODEL
  )
  const TIER_SELECTION_MODEL_IS_PERFORMANCE =
    tierSelectionModel.id === PERFORMANCE_TIER_SELECTION_MODEL.id
  const {
    commissionTiers,
    setCommissionTiers,
    handleTierPropertyChanged,
    getTiersPayload,
    hoveredCommissionTierRowIndex,
    setHoveredCommissionTierRowIndex,
  } = useCommissionTiersUtils(DEFAULT_COMMISSION_TIER)

  // Fees default to the first tier of Settings -> Office, 0 when unset
  const officeDealFee = commissionSplitTiersSetting[0]?.deal_fee || 0
  const officeDeskFee = commissionSplitTiersSetting[0]?.desk_fee || 0
  const [perTransactionFee, setPerTransactionFee] = useState(officeDealFee)
  const [monthlyDeskFee, setMonthlyDeskFee] = useState(officeDeskFee)
  const [annualSalary, setAnnualSalary] = useState(0)
  const [equityParticipation, setEquityParticipation] = useState(false)
  const { selectedPerks, setSelectedPerks } = useSelectedPerks([])
  const {
    selectedPerks: selectedCustomPerks,
    setSelectedPerks: setSelectedCustomPerks,
  } = useSelectedPerks()
  const [note, setNote] = useState("")
  const [sendProposalEmail, setSendProposalEmail] = useState(false)
  const [isCreatingOffer, setIsCreatingOffer] = useState(false)
  const emailProviderAuthorized = useSelector(selectEmailProviderAuthorized)

  const axios = useAuthorizedAxiosClient()
  const FIELD_REQUIRED_ERROR = "This field is required"

  function handleNameChanged(newValue) {
    setName(newValue)
    if (newValue && nameError) {
      setNameError(null)
    }
  }

  function handleIntroductionChanged(newValue) {
    setIntroduction(newValue)
    if (newValue && introductionError) {
      setIntroductionError(null)
    }
  }

  function handleSigningBonusChanged(e) {
    handleChangedPositiveOnlyTextField(e, (newValue) => {
      setSigningBonus(newValue)
      setSigningBonusError(null)
    })
  }

  function handleTierSelectionModelChanged(e) {
    const newModelIsPerformance =
      e.target.value === PERFORMANCE_TIER_SELECTION_MODEL.id
    setTierSelectionModel(
      newModelIsPerformance
        ? PERFORMANCE_TIER_SELECTION_MODEL
        : MANUAL_TIER_SELECTION_MODEL
    )
    if (!newModelIsPerformance) {
      const someTierIsSelected =
        commissionTiers.findIndex((tier) => tier.selected) > -1
      if (!someTierIsSelected) {
        setCommissionTiers(
          commissionTiers.map((tier, i) => ({ ...tier, selected: i === 0 }))
        )
      }
    }
  }

  function handleCommisionTierPropertyChanged(tierIndex, propertyName) {
    return (e) => {
      handleChangedPositiveOnlyTextField(e, (newValue) => {
        let brokerSplitError
        if (propertyName === EDITABLE_COMMISSION_TIER_PROPS.brokerSplit) {
          const minValue = 0
          const maxValue = 90
          if (newValue && (newValue < minValue || newValue > maxValue)) {
            brokerSplitError = `Not from ${minValue} to ${maxValue}`
          }
        }
        handleTierPropertyChanged(tierIndex, propertyName, newValue, {
          broker_split_error: brokerSplitError,
        })
      })
    }
  }

  function handleCommissionTierSelected(tierIndex) {
    setCommissionTiers(
      commissionTiers.map((tier, i) => ({ ...tier, selected: i === tierIndex }))
    )
  }

  function handleTierDeleted(tierIndex) {
    const tierToDelete = commissionTiers[tierIndex]
    const remainingTiers = commissionTiers.filter((tier, i) => i !== tierIndex)
    if (tierToDelete.selected) {
      remainingTiers[0].selected = true
    }
    setCommissionTiers(remainingTiers)
  }

  function handleAddTierButtonClicked() {
    setCommissionTiers([...commissionTiers, DEFAULT_COMMISSION_TIER])
  }

  function handleCreateOfferButtonClicked() {
    const badSigningBonus = signingBonus === ""
    const badCommissionRatio = commissionTiers === ""
    if (!name || !introduction || badSigningBonus || badCommissionRatio) {
      if (!name) {
        setNameError(FIELD_REQUIRED_ERROR)
      }
      if (!introduction) {
        setIntroductionError(FIELD_REQUIRED_ERROR)
      }
      if (badSigningBonus) {
        setSigningBonusError(FIELD_REQUIRED_ERROR)
      }
    } else {
      createOffer()
    }
  }

  function createOffer() {
    setIsCreatingOffer(true)
    const template = {
      name,
      introduction,
      annual_salary: annualSalary,
      signing_bonus: signingBonus,
      equity_participation: equityParticipation,
      tier_selection_model: tierSelectionModel.id,
      commission_tiers: getTiersPayload().filter((tier) => !!tier.broker_split),
      per_transaction_fee: perTransactionFee,
      monthly_desk_fee: monthlyDeskFee,
      perks: selectedPerks.map(({ id }) => id),
      custom_perks: selectedCustomPerks.map(({ id }) => id),
      note,
    }
    return axios
      .post(`/crm/api/offers/create/`, {
        candidate: candidate.id,
        status,
        template,
        send_email: sendProposalEmail,
      })
      .then(({ data }) => {
        setName("")
        setIntroduction("")
        setStatus(OFFER_ORDERED_STATUSES[0])
        setAnnualSalary(0)
        setSigningBonus(0)
        setEquityParticipation(false)
        setTierSelectionModel(PERFORMANCE_TIER_SELECTION_MODEL)
        setCommissionTiers(commissionSplitTiersSetting)
        setPerTransactionFee(officeDealFee)
        setMonthlyDeskFee(officeDeskFee)
        setSelectedPerks([])
        setSelectedCustomPerks([])
        setNote("")
        setSendProposalEmail(false)
        onClose(data)
        SegmentTracker.track("Proposal created", {
          Name: name,
          "Candidate name": candidate.candidate_name,
        })
      })
      .finally(() => setIsCreatingOffer(false))
  }

  useEffect(
    () => {
      setCommissionTiers(commissionSplitTiersSetting)
      setPerTransactionFee(officeDealFee)
      setMonthlyDeskFee(officeDeskFee)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commissionSplitTiersSetting]
  )

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      anchor="right"
      sx={{ zIndex: 1201 }}
      PaperProps={{
        sx: { zIndex: 1201 },
      }}
    >
      <DialogHeader title="Create Proposal" onClose={onClose} />
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          pr: 1,
        }}
      >
        <TitledSection title="Basic Info">
          <LengthLimitedTextField
            label="Name"
            value={name}
            setValue={handleNameChanged}
            maxLength={64}
            required
            error={!!nameError}
            helperText={nameError}
            textFieldProps={{
              InputProps: {
                endAdornment: (
                  <TextFieldTooltipAdornment tooltip="Internal use.  Name the offer something you'll remember." />
                ),
              },
            }}
          />
          <LengthLimitedTextField
            label="Introduction"
            value={introduction}
            setValue={handleIntroductionChanged}
            maxLength={300}
            multiline
            required
            error={!!introductionError}
            helperText={introductionError}
            textFieldProps={{
              InputProps: {
                endAdornment: (
                  <TextFieldTooltipAdornment tooltip="Write a short introductory letter you want to be seen by the candidate." />
                ),
              },
            }}
          />
          <OfferStatusTextField status={status} setStatus={setStatus} />
        </TitledSection>
        <SectionDivider />
        <TitledSection title="Compensation">
          <Grid container spacing={2} xs={12}>
            <Grid xs={12} sm={6}>
              <TextField
                label="Signing Bonus"
                value={signingBonus}
                onChange={handleSigningBonusChanged}
                error={!!signingBonusError}
                helperText={signingBonusError}
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  endAdornment: (
                    <TextFieldTooltipAdornment tooltip="Any one-time cash signing bonus you wish to add to your offer." />
                  ),
                }}
                fullWidth
                required
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Annual Salary"
                value={annualSalary}
                onChange={(e) =>
                  handleChangedPositiveOnlyTextField(e, setAnnualSalary)
                }
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Grid>
            <Grid xs={12}>
              <FormControlLabel
                label="Equity Participation"
                control={
                  <CheckBox
                    value={equityParticipation}
                    onChange={(e) => setEquityParticipation(e.target.checked)}
                  />
                }
              />
            </Grid>
          </Grid>
        </TitledSection>
        <SectionDivider />
        <TitledSection title="Tiers/Caps" gap={2}>
          <FormControl>
            <TierSelectionFormLabel>
              Tier selection model
            </TierSelectionFormLabel>
            <RadioGroup
              row
              value={tierSelectionModel.id}
              onChange={handleTierSelectionModelChanged}
            >
              {Object.values(TIER_SELECTION_MODEL_OPTIONS).map(
                (tierSelectionModel) => (
                  <FormControlLabel
                    value={tierSelectionModel.id}
                    control={<Radio size="small" />}
                    label={tierSelectionModel.label}
                    key={tierSelectionModel.id}
                    sx={{
                      color: "inherit",
                      "& .MuiFormControlLabel-label": { fontSize: "14px" },
                    }}
                  />
                )
              )}
            </RadioGroup>
          </FormControl>
          <Column sx={{ gap: 1 }}>
            <Column sx={{ gap: 3 }}>
              {commissionTiers.map((tier, i) => (
                <Grid
                  sx={{ mb: { xs: "16px", sm: 0 } }}
                  container
                  columnSpacing={2}
                  xs={12}
                  onMouseEnter={() => setHoveredCommissionTierRowIndex(i)}
                  onMouseLeave={() => setHoveredCommissionTierRowIndex(null)}
                  key={i}
                >
                  <Grid xs={12} sm={4} sx={{ mb: { xs: "16px", sm: 0 } }}>
                    <TextField
                      label="Broker Split"
                      value={tier.broker_split}
                      onChange={handleCommisionTierPropertyChanged(
                        i,
                        EDITABLE_COMMISSION_TIER_PROPS.brokerSplit
                      )}
                      error={!!tier.broker_split_error}
                      helperText={tier.broker_split_error}
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <DoubleAdornment tooltip="The percentage share of each transaction that will go to the brokerage." />
                        ),
                      }}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid xs={12} sm={4} sx={{ mb: { xs: "16px", sm: 0 } }}>
                    <TextField
                      label="Agent Split"
                      value={100 - tier.broker_split}
                      disabled
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <DoubleAdornment tooltip="Updates automatically based on Broker Split." />
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <TierThirdColumnRow
                      performanceModelSelected={
                        TIER_SELECTION_MODEL_IS_PERFORMANCE
                      }
                    >
                      {TIER_SELECTION_MODEL_IS_PERFORMANCE ? (
                        <SalesVolumeLimitTextField
                          value={tier.limit}
                          onChange={handleCommisionTierPropertyChanged(
                            i,
                            EDITABLE_COMMISSION_TIER_PROPS.limit
                          )}
                          error={!!tier.limit_error}
                          helperText={tier.limit_error}
                        />
                      ) : (
                        <Column sx={{ position: "relative" }}>
                          {i === 0 ? (
                            <Typography
                              variant="body2"
                              sx={{
                                position: "absolute",
                                top: "-24px",
                                left: "0px",
                                display: { xs: "none", sm: "block" },
                              }}
                            >
                              Select
                            </Typography>
                          ) : null}
                          <Radio
                            checked={tier.selected}
                            value={i}
                            onChange={(e) => handleCommissionTierSelected(i)}
                          />
                        </Column>
                      )}
                      <RemoveCommissionTierIcon
                        index={i}
                        hoveredCommissionTierRowIndex={
                          hoveredCommissionTierRowIndex
                        }
                        commissionTiers={commissionTiers}
                        handleTierDeleted={handleTierDeleted}
                      />
                    </TierThirdColumnRow>
                  </Grid>
                </Grid>
              ))}
            </Column>
            <Collapse in={!TIER_SELECTION_MODEL_IS_PERFORMANCE}>
              <Button
                variant="text"
                color="success"
                size="small"
                fullWidth
                startIcon={
                  <AddCircleOutlineOutlinedIcon
                    sx={{ color: "success.main" }}
                  />
                }
                onClick={handleAddTierButtonClicked}
              >
                Add tier
              </Button>
            </Collapse>
          </Column>
        </TitledSection>
        <SectionDivider />
        <TitledSection title="Fees">
          <Grid
            container
            spacing={3}
            /* combats horizontal scrollbars */ xs={12}
          >
            <Grid xs={12} sm={6}>
              <PerTransactionFeeTextField
                value={perTransactionFee}
                onChange={(e) =>
                  handleChangedPositiveOnlyTextField(e, setPerTransactionFee)
                }
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <MonthlyDeskFeeTextField
                value={monthlyDeskFee}
                onChange={(e) =>
                  handleChangedPositiveOnlyTextField(e, setMonthlyDeskFee)
                }
              />
            </Grid>
          </Grid>
        </TitledSection>
        <SectionDivider />
        <TitledSection
          title={
            <Row component="span" alignItems="center" gap={1}>
              <Typography variant="h6">Perks</Typography>
              <Typography variant="h6" component="span" color="text.secondary">
                {selectedPerks.length + selectedCustomPerks.length} selected
              </Typography>
              <Tooltip title="Tell the candidate about some of the other benefits of working at your office.">
                <TooltipIcon />
              </Tooltip>
            </Row>
          }
        >
          <PerksEditor
            selectedPerks={selectedPerks}
            setSelectedPerks={setSelectedPerks}
            selectedCustomPerks={selectedCustomPerks}
            setSelectedCustomPerks={setSelectedCustomPerks}
          />
        </TitledSection>
        <SectionDivider />
        <TitledSection title="Note">
          <LengthLimitedTextField
            label="Note"
            maxLength={200}
            value={note}
            setValue={setNote}
            multiline
            textFieldProps={{
              InputProps: {
                endAdornment: (
                  <TextFieldTooltipAdornment tooltip="Any other helpful information you'd like the candidate to know, such as information about tiers, caps, etc." />
                ),
              },
            }}
          />
        </TitledSection>
        <TitledSection title="Send email">
          <FormControlLabel
            label="Send proposal email to candidate"
            control={
              <CheckBox
                value={sendProposalEmail}
                onChange={(e) => setSendProposalEmail(e.target.checked)}
                disabled={!emailProviderAuthorized}
              />
            }
          />
        </TitledSection>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={() => onClose()}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleCreateOfferButtonClicked}
          disabled={
            !!(
              nameError ||
              introductionError ||
              signingBonusError ||
              commissionTiers.filter(
                (tier) => tier.broker_split_error || tier.limit_error
              ).length
            )
          }
          loading={isCreatingOffer}
        >
          Create proposal
        </LoadingButton>
      </DialogActions>
    </Drawer>
  )
}

function TextFieldTooltipAdornment({ tooltip }) {
  return (
    <InputAdornment position="end">
      <Tooltip title={tooltip}>
        <TooltipIcon />
      </Tooltip>
    </InputAdornment>
  )
}

function DoubleAdornment({ tooltip }) {
  return (
    <BigInputAdornment position="end">
      <Typography variant="body2" color="text.secondary">
        %
      </Typography>
      <Tooltip title={tooltip}>
        <TooltipIcon />
      </Tooltip>
    </BigInputAdornment>
  )
}

const BigInputAdornment = styled(InputAdornment)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}))

const TooltipIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  fill: theme.palette.colors.gray400,
  "&:hover": {
    fill: theme.palette.colors.gray500,
  },
}))

export const PerTransactionFeeTextField = styled((props) => (
  <TextField
    label="Per Transaction Fee"
    type="number"
    InputProps={{
      startAdornment: <InputAdornment position="start">$</InputAdornment>,
      endAdornment: (
        <TextFieldTooltipAdornment tooltip="Any fees your brokerage charges to the agent per transaction." />
      ),
    }}
    fullWidth
    {...props}
  />
))(() => ({}))

export const MonthlyDeskFeeTextField = styled((props) => (
  <TextField
    label="Monthly Desk Fee"
    type="number"
    InputProps={{
      startAdornment: <InputAdornment position="start">$</InputAdornment>,
      endAdornment: (
        <TextFieldTooltipAdornment tooltip="Any ongoing fees your brokerage charges." />
      ),
    }}
    fullWidth
    {...props}
  />
))(() => ({}))

export const SalesVolumeLimitTextField = styled((props) => (
  <TextField
    label="Up To Limit"
    type="text"
    InputProps={{
      startAdornment: <InputAdornment position="start">$</InputAdornment>,
    }}
    fullWidth
    {...props}
  />
))(() => ({}))

export function RemoveCommissionTierIcon({
  index,
  hoveredCommissionTierRowIndex,
  commissionTiers,
  handleTierDeleted,
  right = -20,
}) {
  return commissionTiers.length > 1 ? (
    <Fade in={hoveredCommissionTierRowIndex === index}>
      <HighlighOffIcon
        sx={{
          cursor: "pointer",
          position: "absolute",
          top: "30%",
          right,
          fontSize: "medium",
          fill: (theme) => theme.palette.error.main,
        }}
        onClick={() => handleTierDeleted(index)}
      />
    </Fade>
  ) : null
}

export const INFINITY_SIGN = "∞"

class CommssionTier {
  constructor(
    brokerSplit = 0,
    limit = 0,
    selected = false,
    brokerSplitError = null,
    limitError = null
  ) {
    this.broker_split = brokerSplit
    this.limit = limit
    this.selected = selected
    this.broker_split_error = brokerSplitError
    this.limit_error = limitError
  }
}

const EDITABLE_COMMISSION_TIER_PROPS = {
  brokerSplit: "broker_split",
  limit: "limit",
  selected: "selected",
}

export function useCommissionTiersUtils(DEFAULT_COMMISSION_TIER) {
  const [commissionTiers, setCommissionTiers] = useState([
    DEFAULT_COMMISSION_TIER,
  ])
  const [hoveredCommissionTierRowIndex, setHoveredCommissionTierRowIndex] =
    useState(null)

  function handleTierPropertyChanged(
    tierIndex,
    propertyName,
    newValue,
    extraErrors
  ) {
    let limitError
    if (propertyName === EDITABLE_COMMISSION_TIER_PROPS.limit) {
      const previousTier = commissionTiers[tierIndex - 1]
      if (previousTier && newValue <= parseInt(previousTier.limit)) {
        limitError = `Must be > ${parseInt(
          previousTier.limit
        ).toLocaleString()}`
      }
    }
    if (
      propertyName === EDITABLE_COMMISSION_TIER_PROPS.limit &&
      !!newValue &&
      isNaN(parseInt(newValue))
    ) {
      return
    }
    let newCommissionTiers = commissionTiers.map((tier, index) =>
      index === tierIndex
        ? {
            ...tier,
            [propertyName]: newValue,
            limit_error: limitError,
            ...extraErrors,
          }
        : tier
    )
    const newCommissionTierCount = filterFilledCommissionTiers().length + 1
    if (newCommissionTierCount > newCommissionTiers.length) {
      newCommissionTiers.push(
        Object.assign(DEFAULT_COMMISSION_TIER, { limit: INFINITY_SIGN })
      )
    }
    setCommissionTiers(newCommissionTiers)
  }

  function handleTierDeleted(tierIndex) {
    setCommissionTiers(commissionTiers.filter((tier, i) => i !== tierIndex))
  }

  function limitHasValue(tier) {
    return (
      [undefined, INFINITY_SIGN].indexOf(tier.limit) === -1 &&
      parseInt(tier.limit) > 0
    )
  }

  function filterFilledCommissionTiers() {
    return commissionTiers.filter(
      (tier) => !!tier.broker_split && limitHasValue(tier)
    )
  }

  function getTiersPayload() {
    return commissionTiers.map((tier) => {
      const tierCopy = { ...tier }
      tierCopy.broker_split = parseInt(tier.broker_split)
      tierCopy.deal_fee = parseInt(tier.deal_fee)
      tierCopy.desk_fee = parseInt(tier.desk_fee)
      if (limitHasValue(tierCopy)) {
        tierCopy.limit = parseInt(tier.limit)
      } else {
        tierCopy.limit = undefined
      }
      return tierCopy
    })
  }

  return {
    commissionTiers,
    setCommissionTiers,
    handleTierPropertyChanged,
    handleTierDeleted,
    limitHasValue,
    getTiersPayload,
    hoveredCommissionTierRowIndex,
    setHoveredCommissionTierRowIndex,
  }
}

export function useCreateOfferDialogState() {
  const [createOfferDialogOpen, setCreateOfferDialogOpen] = useState(false)
  const { runOnlyIfSubscribed } = useTrialFinishedDialogUtils()

  function openCreateOfferDialog() {
    runOnlyIfSubscribed(
      () => setCreateOfferDialogOpen(true),
      new TrialFinishedRoadblockDialogData(
        "Creating Offer",
        "Subscribe to create and send offers to your candidates"
      )
    )
  }

  return {
    createOfferDialogOpen,
    setCreateOfferDialogOpen,
    openCreateOfferDialog,
  }
}

const TierSelectionFormLabel = styled(FormLabel)(({ theme }) => ({
  paddingInline: 0,
  fontSize: "14px",
  fontWeight: 500,
  color: "inherit",
}))

const TierThirdColumnRow = styled(Row, {
  shouldForwardProp: (prop) => prop !== "performanceModelSelected",
})(({ theme, performanceModelSelected }) => ({
  position: "relative",
  width: "100%",
  justifyContent: performanceModelSelected ? "initial" : "center",
  alignItems: "center",
}))

function SectionDivider() {
  return <Divider sx={{ my: 3 }} />
}

function optionalParseInt(value) {
  const parsedInt = parseInt(value)
  return isNaN(parsedInt) ? "" : parsedInt
}

function handleChangedPositiveOnlyTextField(e, onPositive) {
  const newValue = optionalParseInt(e.target.value)
  if (newValue < 0) {
    e.preventDefault()
  } else {
    onPositive(newValue)
  }
}

class TierSelectionModel {
  constructor(id, label) {
    this.id = id
    this.label = label
  }
}

const PERFORMANCE_TIER_SELECTION_MODEL = new TierSelectionModel(
  "performance",
  "Candidate performance/sales (Automatic)"
)
const MANUAL_TIER_SELECTION_MODEL = new TierSelectionModel("manual", "Manual")

const TIER_SELECTION_MODEL_OPTIONS = {
  performance: PERFORMANCE_TIER_SELECTION_MODEL,
  manual: MANUAL_TIER_SELECTION_MODEL,
}
